// src/lib/use-pricing-config-list.js — Phase 6 PR3 (ADR-094)
//
// Hook React qui charge la liste des configs pricing par logement (CRUD).
// Méthodes exposées : refetch, upsert (POST/PATCH avec If-Match), remove (soft).
//
// Convention codebase : src/lib/use-*.js (cf. use-photo-upload-queue.js — seul précédent formel).

import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "../supabase";

/**
 * Liste les configs pricing par logement (CRUD côté serveur).
 *
 * @returns {{
 *   configs: Array,
 *   dryRunActive: boolean,
 *   manualCounts: Object<string,{epingles:(number|null), manualPushSet:(number|null)}>|null,
 *   manualCountsEtat: 'chargement'|'recus'|'indisponible',
 *   manualCountsWindow: {from:string, to:string, horizon_days:number}|null,
 *   loading: boolean,
 *   error: string|null,
 *   refetch: () => Promise<void>,
 *   upsert: (payload, opts?: { ifMatch?: string }) => Promise<{ok:boolean, status:number, conflict?:boolean, data?:any, error?:string, seed?:{count:number, success:boolean, error?:string}}>,
 *   remove: (logement_id: string) => Promise<{ok:boolean, error?:string}>,
 * }}
 */
export function usePricingConfigList() {
  const [configs, setConfigs] = useState([]);
  const [dryRunActive, setDryRunActive] = useState(true);
  // Garde « Force resync » (2026-09-28) — comptes de dates MANUELLES par
  // logement, calculés côté SERVEUR (service_role). `null` = pas encore reçu,
  // ou serveur incapable de les établir : JAMAIS un zéro par défaut.
  //
  // ⚠️ Ces comptes ne peuvent PAS être refaits ici : `daily_pricing` est sous
  // RLS `service_role` seul, et le rôle `authenticated` du navigateur reçoit
  // 0 ligne SANS erreur. Un `count` venu d'une table que le client ne peut pas
  // lire n'est jamais un fait.
  const [manualCounts, setManualCounts] = useState(null);
  // « je compte » et « je n'ai pas pu compter » ne sont PAS le même état : le
  // second rendu comme le premier ferait tourner un spinner à vie sur une
  // cause qui ne se résoudra pas. `manualCounts === null` ne suffit donc pas.
  const [manualCountsEtat, setManualCountsEtat] = useState("chargement");
  const [manualCountsWindow, setManualCountsWindow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    setManualCountsEtat("chargement");
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("not_authenticated");
      const res = await fetch(
        "/api/beds24-mapping?action=pricing_logement_config",
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `http_${res.status}`);
      }
      const json = await res.json();
      if (!mountedRef.current) return;
      setConfigs(json.configs || []);
      setDryRunActive(json.dry_run_active !== false);
      const recus =
        json.manual_counts && typeof json.manual_counts === "object";
      setManualCounts(recus ? normaliseManualCounts(json.manual_counts) : null);
      setManualCountsEtat(recus ? "recus" : "indisponible");
      setManualCountsWindow(json.manual_counts_window || null);
    } catch (e) {
      if (mountedRef.current) {
        setError(e.message || String(e));
        // La liste n'a pas pu être lue ⇒ les comptes non plus. On ne garde
        // surtout pas les anciens : périmés, ils mentiraient avec aplomb.
        setManualCounts(null);
        setManualCountsEtat("indisponible");
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const upsert = useCallback(
    async (payload, { ifMatch } = {}) => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token)
          return { ok: false, status: 401, error: "not_authenticated" };
        const isPatch = !!payload._existing;
        const method = isPatch ? "PATCH" : "POST";
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        };
        if (isPatch && ifMatch) headers["If-Match"] = ifMatch;
        // strip _existing flag before send
        const body = { ...payload };
        delete body._existing;
        const res = await fetch(
          "/api/beds24-mapping?action=pricing_logement_config",
          { method, headers, body: JSON.stringify(body) },
        );
        const json = await res.json().catch(() => ({}));
        if (res.status === 412) {
          return {
            ok: false,
            status: 412,
            conflict: true,
            error: json.message || "precondition_failed",
          };
        }
        if (!res.ok) {
          return {
            ok: false,
            status: res.status,
            error: json.error || json.message || `http_${res.status}`,
          };
        }
        await fetchList();
        return {
          ok: true,
          status: res.status,
          data: json.logement_pricing_config,
          // PR3 commit 11 : seed initial Beds24 (présent uniquement sur POST
          // mode=manual avec mapping résolu, undefined sinon).
          seed: json.seed,
        };
      } catch (e) {
        return { ok: false, status: 0, error: e.message || String(e) };
      }
    },
    [fetchList],
  );

  const remove = useCallback(
    async (logement_id) => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token)
          return { ok: false, error: "not_authenticated" };
        const res = await fetch(
          `/api/beds24-mapping?action=pricing_logement_config&logement_id=${encodeURIComponent(logement_id)}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${session.access_token}` },
          },
        );
        if (!res.ok) {
          const json = await res.json().catch(() => ({}));
          return {
            ok: false,
            error: json.error || json.message || `http_${res.status}`,
          };
        }
        await fetchList();
        return { ok: true };
      } catch (e) {
        return { ok: false, error: e.message || String(e) };
      }
    },
    [fetchList],
  );

  return {
    configs,
    dryRunActive,
    manualCounts,
    manualCountsEtat,
    manualCountsWindow,
    loading,
    error,
    refetch: fetchList,
    upsert,
    remove,
  };
}

/**
 * Normalise `manual_counts` du serveur en clés JS. Un compteur absent ou non
 * numérique reste `null` — « je ne sais pas », jamais « zéro ».
 *
 * @param {Object} brut
 * @returns {Object<string,{epingles:(number|null), manualPushSet:(number|null)}>}
 */
function normaliseManualCounts(brut) {
  const out = {};
  for (const [id, v] of Object.entries(brut)) {
    const e = v && typeof v.epingles === "number" ? v.epingles : null;
    const m =
      v && typeof v.manual_push_set === "number" ? v.manual_push_set : null;
    out[id] = { epingles: e, manualPushSet: m };
  }
  return out;
}
